import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import CreateGodMemberForm from '../components/CreateGodMemberForm';
import EditGodMemberForm from '../components/EditGodMemberForm';
import MemberDetailsModal from '../components/MemberDetailsModal';
import GodViewMediaModal from '../components/GodViewMediaModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import Pagination from '../components/Pagination';
import MemberWebAnalyticsSparkline from '../components/MemberWebAnalyticsSparkline';
import axios from 'axios';
import { godMemberStatusIcon } from '../utils/reviewLabels';
import { formatSingaporeDateDmy } from '../utils/singaporeTime';
import { FiPlus, FiChevronLeft, FiChevronRight, FiImage, FiEye, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';

const SEARCH_TYPE_OPTIONS = [
    { value: 'name', label: 'Name / ID', placeholder: 'godID, godName, oldName, or webID...' },
    { value: 'phone', label: 'Phone', placeholder: 'Contact, WhatsApp, or TG number...' },
    { value: 'godgroup', label: 'God Group', placeholder: 'God group name...' },
    { value: 'shop', label: 'Shop', placeholder: 'Shop name...' },
];

const GodMemberPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMembers, setTotalMembers] = useState(0);
    const [selectedMember, setSelectedMember] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchType, setSearchType] = useState('name');
    const [searchTypeInput, setSearchTypeInput] = useState('name');
    const [newSetupDoneFilter, setNewSetupDoneFilter] = useState(''); // default: all (no filter)
    const [memberTypeFilter, setMemberTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [photosFilter, setPhotosFilter] = useState('');
    const [updateContactFilter, setUpdateContactFilter] = useState('');
    const [autobotFilter, setAutobotFilter] = useState('');
    const [reviewsFilter, setReviewsFilter] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [recommendToReview, setRecommendToReview] = useState(false);
    const [analyticsByWebId, setAnalyticsByWebId] = useState({});
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const viewModalFetchedIdRef = useRef(null);

    const fetchMembers = async (page, search = '', searchTypeParam = 'name', newSetupDone = '', memberType = '', status = '', photos = '', updateContact = '', reviews = '', sort = '', recommended = recommendToReview, autobot = '') => {
        try {
            setLoading(true);
            const newSetupDoneParam = newSetupDone === 'true' || newSetupDone === 'false' ? `&newSetupDone=${newSetupDone}` : '';
            const memberTypeParam = memberType && (memberType === 'Member' || memberType === 'Admin') ? `&memberType=${encodeURIComponent(memberType)}` : '';
            const statusParam = status && ['active', 'hold', 'expiring', 'expired', 'cancelled', 'changedName', 'stop'].includes(status) ? `&status=${encodeURIComponent(status)}` : '';
            const photosParam = photos === 'true' || photos === 'false' ? `&photos=${photos}` : '';
            const updateContactParam = updateContact === 'true' || updateContact === 'false' ? `&updateContact=${updateContact}` : '';
            const autobotParam = autobot === 'true' || autobot === 'false' ? `&autobot=${autobot}` : '';
            const reviewsParam = reviews === 'yes' || reviews === 'no' ? `&reviews=${reviews}` : '';
            const sortParam = sort && ['renewalDate', '-renewalDate'].includes(sort) ? `&sort=${encodeURIComponent(sort)}` : '';
            const searchParam = search ? `&search=${encodeURIComponent(search)}&searchType=${encodeURIComponent(searchTypeParam)}` : '';
            const recommendParam = recommended ? '&recommendToReview=true' : '';
            const apiUrl = `${process.env.REACT_APP_API_URL}/api/godmembers?page=${page}&limit=20&populate=godTGs,payments,handlerPayments,participantPayments,shop,lastGodGroup${searchParam}${newSetupDoneParam}${memberTypeParam}${statusParam}${photosParam}${updateContactParam}${autobotParam}${reviewsParam}${sortParam}${recommendParam}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setMembers(response.data.data);
                setTotalPages(response.data.totalPages);
                setTotalMembers(response.data.total);
            }
        } catch (err) {
            setError(`Failed to fetch god members: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers(currentPage, searchTerm, searchType, newSetupDoneFilter, memberTypeFilter, statusFilter, photosFilter, updateContactFilter, reviewsFilter, sortBy, recommendToReview, autobotFilter);
    }, [currentPage, searchTerm, searchType, newSetupDoneFilter, memberTypeFilter, statusFilter, photosFilter, updateContactFilter, reviewsFilter, sortBy, recommendToReview, autobotFilter]);

    useEffect(() => {
        const webIDs = [...new Set(members.map((m) => m.webID).filter(Boolean))];
        if (!webIDs.length) {
            setAnalyticsByWebId({});
            setAnalyticsLoading(false);
            return;
        }

        let cancelled = false;
        const fetchAnalytics = async () => {
            setAnalyticsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/slg-analytics/members`,
                    {
                        params: { webIDs: webIDs.join(','), days: 30 },
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true',
                        },
                    }
                );
                if (!cancelled && response.data.success) {
                    setAnalyticsByWebId(response.data.data || {});
                }
            } catch {
                if (!cancelled) setAnalyticsByWebId({});
            } finally {
                if (!cancelled) setAnalyticsLoading(false);
            }
        };

        fetchAnalytics();
        return () => {
            cancelled = true;
        };
    }, [members]);

    // Open create form when navigated with ?open=create (e.g. from Layout action bar)
    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreateForm(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Two-phase load: fast summary first so modal appears quickly, then full payment data in background
    useEffect(() => {
        if (!showViewModal || !selectedMember?._id) return;
        const memberId = selectedMember._id;
        if (viewModalFetchedIdRef.current === memberId) return;
        viewModalFetchedIdRef.current = memberId;

        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' };
        const baseUrl = `${process.env.REACT_APP_API_URL}/api/godmembers/${memberId}`;

        // 1) Fast summary – show modal quickly (no payments)
        axios.get(`${baseUrl}?view=summary`, { headers })
            .then((res) => {
                if (res.data.success) {
                    setSelectedMember(res.data.data);
                }
            })
            .catch((err) => {
                setError(`Failed to fetch member details: ${err.response?.data?.error || err.message}`);
            })
            .finally(() => setViewDetailsLoading(false));

        // 2) Full data in background – payments, paymentHistory, etc.
        axios.get(baseUrl, { headers })
            .then((res) => {
                if (res.data.success) {
                    setSelectedMember((prev) => (prev && prev._id === memberId ? res.data.data : prev));
                }
            })
            .catch(() => {
                // Non-blocking: summary already shown; payment section will show loading/empty
            });
    }, [showViewModal, selectedMember?._id]);

    const handleCreateSuccess = (newMember) => {
        setShowCreateForm(false);
        fetchMembers(currentPage, searchTerm, searchType, newSetupDoneFilter, memberTypeFilter, statusFilter, photosFilter, updateContactFilter, reviewsFilter, sortBy, recommendToReview, autobotFilter);
    };

    const handleEditSuccess = (updatedMember) => {
        setShowEditModal(false);
        setSelectedMember(null);
        fetchMembers(currentPage, searchTerm, searchType, newSetupDoneFilter, memberTypeFilter, statusFilter, photosFilter, updateContactFilter, reviewsFilter, sortBy, recommendToReview, autobotFilter);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const formatLatestTgPost = (member) => {
        const latest = (member.godTGs || [])
            .flatMap((tg) => (tg.postHistory || []).map((post) => ({ ...post, tg })))
            .filter((post) => post.date)
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        if (!latest) return 'N/A';
        const totalCount = (latest.groupPosts || []).reduce((sum, post) => sum + (post.count || 0), 0);
        return `${formatDate(latest.date)}${totalCount > 0 ? ` (${totalCount})` : ''}`;
    };

    const sameCalendarDay = (a, b) => {
        if (!a || !b) return false;
        const da = new Date(a);
        const db = new Date(b);
        return (
            da.getFullYear() === db.getFullYear()
            && da.getMonth() === db.getMonth()
            && da.getDate() === db.getDate()
        );
    };

    const handleView = (member) => {
        setSelectedMember(member);
        setShowViewModal(true);
        setViewDetailsLoading(true);
    };

    const handleEdit = async (member) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers/${member._id}?view=edit`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setSelectedMember(response.data.data);
                setShowEditModal(true);
            }
        } catch (err) {
            setError(`Failed to fetch member for edit: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = (member) => {
        setSelectedMember(member);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/godmembers/${selectedMember._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedMember(null);
            fetchMembers(currentPage, searchTerm, searchType, newSetupDoneFilter, memberTypeFilter, statusFilter, photosFilter, updateContactFilter, reviewsFilter, sortBy, recommendToReview, autobotFilter);
        } catch (err) {
            setError(`Failed to delete member: ${err.response?.data?.error || err.message}`);
        }
    };

    // Handle search button click
    const handleSearch = () => {
        setRecommendToReview(false);
        setSearchTerm(searchInput.trim());
        setSearchType(searchTypeInput);
        setCurrentPage(1);
    };

    // Handle clear button click
    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setSearchTypeInput('name');
        setSearchType('name');
        setNewSetupDoneFilter('false');
        setMemberTypeFilter('');
        setStatusFilter('');
        setPhotosFilter('');
        setUpdateContactFilter('');
        setAutobotFilter('');
        setReviewsFilter('');
        setSortBy('');
        setRecommendToReview(false);
        setCurrentPage(1);
    };

    const handleRecommendToReview = () => {
        setSearchInput('');
        setSearchTerm('');
        setSearchTypeInput('name');
        setSearchType('name');
        setNewSetupDoneFilter('');
        setMemberTypeFilter('');
        setStatusFilter('active');
        setPhotosFilter('');
        setUpdateContactFilter('');
        setAutobotFilter('');
        setReviewsFilter('');
        setSortBy('');
        setRecommendToReview(true);
        setCurrentPage(1);
    };

    // Add function to generate page numbers
    const getPageNumbers = () => {
        const delta = 2; // Number of pages to show before and after current page
        const range = [];
        const rangeWithDots = [];

        // Always show first page
        range.push(1);

        // Calculate range around current page
        for (let i = currentPage - delta; i <= currentPage + delta; i++) {
            if (i > 1 && i < totalPages) {
                range.push(i);
            }
        }

        // Always show last page
        if (totalPages > 1) {
            range.push(totalPages);
        }

        // Add page numbers with dots
        let prev = 0;
        for (const i of range) {
            if (prev + 1 !== i) {
                rangeWithDots.push('...');
            }
            rangeWithDots.push(i);
            prev = i;
        }

        return rangeWithDots;
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">God Members</h1>
                        <p className="text-gray-600">Total: {totalMembers} members</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Member
                    </button>
                </div>

                {/* Row 1: search input, search type, search button, Clear button */}
                <div className="mb-3 flex flex-nowrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[180px] max-w-[400px]">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                            placeholder={SEARCH_TYPE_OPTIONS.find((o) => o.value === searchTypeInput)?.placeholder || 'Search...'}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                    <select
                        value={searchTypeInput}
                        onChange={(e) => setSearchTypeInput(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                        title="Search by"
                    >
                        {SEARCH_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <button
                        onClick={handleSearch}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center shrink-0"
                    >
                        <FiSearch className="mr-2" />
                        Search
                    </button>
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center shrink-0"
                    >
                        Clear
                    </button>
                    <button
                        onClick={handleRecommendToReview}
                        className={`px-4 py-2 rounded-md flex items-center shrink-0 text-white ${recommendToReview ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    >
                        Sort by Recommend to Review
                    </button>
                </div>

                {/* Row 2: filters (newSetupDone first, then type, status, PhotoUpdate, Update Contact, Sort) */}
                <div className="mb-6">
                    <div className="flex flex-nowrap gap-2 items-center overflow-x-auto">
                        <select
                            value={newSetupDoneFilter}
                            onChange={(e) => { setRecommendToReview(false); setNewSetupDoneFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                            title="New setup done"
                        >
                            <option value="false">New setup: Not done</option>
                            <option value="true">New setup: Done</option>
                            <option value="">New setup: All</option>
                        </select>
                        <select
                            value={memberTypeFilter}
                            onChange={(e) => { setRecommendToReview(false); setMemberTypeFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                        >
                            <option value="">All types</option>
                            <option value="Member">Member</option>
                            <option value="Admin">Admin</option>
                        </select>
                        <select
                            value={statusFilter}
                            onChange={(e) => { setRecommendToReview(false); setStatusFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="hold">Hold</option>
                            <option value="stop">Stop</option>
                            <option value="expiring">Expiring</option>
                            <option value="expired">Expired</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="changedName">Changed Name</option>
                        </select>
                        <select
                            value={reviewsFilter}
                            onChange={(e) => { setRecommendToReview(false); setReviewsFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                            title="Has reviews"
                        >
                            <option value="">Reviews: All</option>
                            <option value="yes">Reviews: Yes</option>
                            <option value="no">Reviews: No</option>
                        </select>
                        <select
                            value={photosFilter}
                            onChange={(e) => { setRecommendToReview(false); setPhotosFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                            title="PhotoUpdate"
                        >
                            <option value="">PhotoUpdate: All</option>
                            <option value="true">PhotoUpdate: Done</option>
                            <option value="false">PhotoUpdate: Not Done</option>
                        </select>
                        <select
                            value={updateContactFilter}
                            onChange={(e) => { setRecommendToReview(false); setUpdateContactFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                        >
                            <option value="">Update Contact: All</option>
                            <option value="true">Update Contact: Done</option>
                            <option value="false">Update Contact: Not Done</option>
                        </select>
                        <select
                            value={autobotFilter}
                            onChange={(e) => { setRecommendToReview(false); setAutobotFilter(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                            title="Autobot"
                        >
                            <option value="">Autobot: All</option>
                            <option value="true">Autobot: Yes</option>
                            <option value="false">Autobot: No</option>
                        </select>
                        <select
                            value={sortBy}
                            onChange={(e) => { setRecommendToReview(false); setSortBy(e.target.value); setCurrentPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white shrink-0"
                            title="Sort by"
                        >
                            <option value="">Sort: Default</option>
                            <option value="-renewalDate">Sort: Renewal Date (newest)</option>
                            <option value="renewalDate">Sort: Renewal Date (oldest)</option>
                        </select>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        Enter a term, choose search type (Name searches godID/godName/oldName/webID; Phone, God Group, Shop), then click Search. Filters apply to new setup, type, status, Reviews, PhotoUpdate, Update Contact, Autobot. Recommended review shows active members whose latest TG post is from 3 days ago through tomorrow, with 0 reviews first, then oldest last review post date.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Fullname
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Shop
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            St
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Renewal Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last God Group
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="New Setup Done / Photos / Update Contact / Autobot">
                                            Done/P/C/AB
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Review
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Last TG Review
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Last Web Review
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Analytics
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Last Post (TG)
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="12" className="px-6 py-4 text-center">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : members.length === 0 ? (
                                        <tr>
                                            <td colSpan="12" className="px-6 py-4 text-center">
                                                No god members found
                                            </td>
                                        </tr>
                                    ) : (
                                        members.map((member) => (
                                            <tr key={member._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm">
                                                    {member.fullName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {member.shop?.map(shop => (
                                                        <div key={shop._id} className="mb-1">
                                                            <div className="font-medium">{shop.shopName}</div>
                                                            <div className="text-sm text-gray-600">
                                                                {shop.region},{shop.area}
                                                            </div>
                                                        </div>
                                                    )) || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    {godMemberStatusIcon(member.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {formatDate(member.renewalDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {member.lastGodGroup ? (
                                                        <div>
                                                            <div className="font-medium">{member.lastGodGroup.groupName}</div>
                                                            <div className={`text-sm ${sameCalendarDay(member.lastGodGroup.renewalDate, member.renewalDate) ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                                                Renewal: {formatDate(member.lastGodGroup.renewalDate)}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center" title="New Setup Done / Photos / Update Contact / Autobot">
                                                    {member.newSetupDone ? '✅' : '❌'}{member.photos ? '✅' : '❌'}{member.updateContact ? '✅' : '❌'}{member.autobot ? '✅' : '❌'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                    {member.reviewCount ?? 0}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatSingaporeDateDmy(member.lastReviewDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatSingaporeDateDmy(member.lastWebReviewDate)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <MemberWebAnalyticsSparkline
                                                        loading={analyticsLoading && Boolean(member.webID)}
                                                        data={member.webID ? analyticsByWebId[member.webID] : null}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatLatestTgPost(member)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => { setSelectedMember(member); setShowMediaModal(true); }}
                                                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full inline-flex items-center"
                                                        title="View Media"
                                                    >
                                                        <FiImage className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleView(member)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(member)}
                                                        className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Member"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(member)}
                                                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Member"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination 
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />

                        {/* Media Modal */}
                        {showMediaModal && selectedMember && (
                            <GodViewMediaModal
                                member={selectedMember}
                                onClose={() => setShowMediaModal(false)}
                                onMediaUpdate={() => fetchMembers(currentPage, searchTerm, searchType, newSetupDoneFilter, memberTypeFilter, statusFilter, photosFilter, updateContactFilter, reviewsFilter, sortBy, recommendToReview, autobotFilter)}
                            />
                        )}

                        {/* View Modal */}
                        {showViewModal && selectedMember && (
                            <MemberDetailsModal
                                member={selectedMember}
                                detailsLoading={viewDetailsLoading}
                                onClose={() => {
                                    setShowViewModal(false);
                                    setSelectedMember(null);
                                    setViewDetailsLoading(false);
                                    viewModalFetchedIdRef.current = null;
                                }}
                            />
                        )}

                        {/* Delete Confirmation Modal */}
                        {showDeleteConfirm && selectedMember && (
                            <DeleteConfirmationModal
                                itemName={selectedMember.godName}
                                onConfirm={confirmDelete}
                                onCancel={() => setShowDeleteConfirm(false)}
                            />
                        )}

                        {/* Edit Modal */}
                        {showEditModal && selectedMember && (
                            <EditGodMemberForm
                                member={selectedMember}
                                onSuccess={handleEditSuccess}
                                onCancel={() => {
                                    setShowEditModal(false);
                                    setSelectedMember(null);
                                }}
                            />
                        )}

                {/* Create Member Modal (popup) - yellow only on popup, gray backdrop */}
                {showCreateForm && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-8 pb-12">
                        <div className="relative max-w-5xl w-full mx-4 bg-amber-50 rounded-lg shadow-lg p-6">
                            <CreateGodMemberForm
                                onSuccess={handleCreateSuccess}
                                onCancel={() => setShowCreateForm(false)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default GodMemberPage; 